import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import { AxiosResponse } from "axios";
import { useAtom } from "jotai";
import { FC, useEffect, useState } from "react";
import { modalImportAnswers, modalInfoForm, permissionsAtom } from "../../../atoms/atom";
import { FormService } from "../../../services/FormService";
import { FormDTO } from "../../../types/FormDTO";
import { useUtils } from "../../../utils/utilFunctions";
import { useAlert } from "../../contextProviders/AlertProvider";
import './InfoFormModal.scss';

const InfoFormModal: FC<Partial<FormDTO>> = (dto: Partial<FormDTO>) => {
	const [open, setOpen] = useAtom(modalInfoForm);
	const [, setOpenImport] = useAtom(modalImportAnswers);
	const [loadingResult, setLoadingResult] = useState(false);
	const [loadingRisks, setLoadingRisks] = useState(false);
	const [loadingInventary, setLoadingInventary] = useState(false);
	const [permissions] = useAtom(permissionsAtom);
	const { showAlert, showError } = useAlert();
	const { verifyPermission } = useUtils();
	const service = new FormService();

	const handleDownloadResult = async () => {
		setLoadingResult(true);
		if (!dto.id) return;
		try {
			const response = await service.downloadResult(dto.id);
			getDownload(response, "COPSOQ II - " + dto.companyName);
		} catch (err) {
			showError("Erro ao baixar arquivo");
		} finally {
			setLoadingResult(false);
		}
	};

	const handleDownloadRisks = async () => {
		setLoadingRisks(true);
		if (!dto.id) return;
		try {
			const response = await service.downloadRisks(dto.id);
			getDownload(response, "DIAGNÓSTICO DE RISCOS PSICOSSOCIAIS - " + dto.companyName);
		} catch (err) {
			showError("Erro ao baixar arquivo");
		} finally {
			setLoadingRisks(false);
		}
	};

	const handleDownloadInventary = async () => {
		setLoadingInventary(true);
		if (!dto.id) return;
		try {
			const response = await service.downloadInventary(dto.id);
			if (response.status === 200) {
				showAlert("Acompanhe o download do arquivo no botão de downloads", "success");
			}
		} catch (err) {
			showError("Erro ao baixar arquivo");
		} finally {
			setLoadingInventary(false);
			setOpen(false);
		}
	};

	useEffect(() => {
		if (!!permissions.length) {
			verifyPermission(permissions, 'FORMS');
		}
	}, []);

	return (
		<Dialog
			open={open}
			onClose={() => setOpen(false)}
			id='modal-info'
		>
			<DialogTitle>Informações sobre o formulário</DialogTitle>
			<DialogContent id="modal-info-content">
				<Typography variant="h6">
					Segundo identificador: <u>{getId(dto.secondId ?? "")}</u>
				</Typography>
				<div id="form-info-buttons">
					{!permissions.includes('CONCLUDE_FORMS')
						? <></>
						: <Button
							variant="contained"
							color="success"
							onClick={() => conclude()}
							sx={{width: "380px", height: "50px"}}
						>
							{dto.active ? "Concluir " : "Reabrir"} formulário
						</Button>
					}
					<Button
						variant="contained"
						color="info"
						onClick={handleDownloadResult}
						sx={{width: "380px", height: "50px"}}
						disabled={loadingResult}
					>
						{loadingResult
							? <CircularProgress sx={{color: "#292929ff"}} size={'25px'}/>
							: "Baixar planilha de resultados"
						}
					</Button>
					<Button
						variant="contained"
						color="info"
						onClick={handleDownloadRisks}
						sx={{width: "380px", height: "50px"}}
						disabled={loadingRisks}
					>
						{loadingRisks
							? <CircularProgress sx={{color: "#292929ff"}} size={'25px'}/>
							: "Baixar planilha de Riscos psicossociais"
						}
					</Button>
					<Button
						variant="contained"
						color="info"
						onClick={handleDownloadInventary}
						sx={{width: "380px", height: "50px"}}
						disabled={loadingInventary}
					>
						{loadingInventary
							? <CircularProgress sx={{color: "#292929ff"}} size={'25px'}/>
							: "Baixar planilha de Inventário de riscos"
						}
					</Button>
					{!permissions.includes('IMPORT_FORMS')
						? <></>
						: <Button
							variant="contained"
							onClick={() => setOpenImport(true)}
							sx={{width: "380px", height: "50px", backgroundColor: "#b3a83f"}}
						>
							Importar respostas
						</Button>
					}
				</div>
			</DialogContent>
			<DialogActions>
				<Button id="close-form-info" onClick={() => setOpen(false)}>
					Fechar
				</Button>
			</DialogActions>
		</Dialog>
	);

	function getId(id: string) {
		switch (id) {
			case "MATRICULA":
				return "Matrícula";
			case "RH_CODE":
				return "Código RH";
			case "BIRTH_DATE":
				return "Data de nascimento";
			default:
				return "Id funcionário";
		}
	}

	async function conclude() {
		const word = dto.active ? "concluído" : "reaberto";
		try {
			if (!dto.id) return;
			const service = new FormService();
			const response = await service.conclude(dto.id);
			if (response?.status === 200) {
				showAlert(`Formulário ${word} com sucesso`, "success");
			} else {
				showError();
			}
		} catch (error) {
			showError();
		} finally {
			setOpen(false);
		}
	}
}

export function getDownload(response: AxiosResponse<any, any>, name: string) {
	const url = window.URL.createObjectURL(new Blob([response.data]));
	const link = document.createElement("a");
	link.href = url;
	link.setAttribute("download", `${name}.xlsx`);
	document.body.appendChild(link);
	link.click();
	link.remove();
	window.URL.revokeObjectURL(url);
}

export default InfoFormModal;